// https://www.figma.com/design/ynKceAEQKn9U5PXlvlYM84/Grid-Finder?node-id=2500-7171&t=53WqtppcoOK2dc05-4
// This card is for the frontend championship card (!isSingleEvent)
'use client'

import { getPlatformName } from '@/actions/platform'
import CalendarIcon from '@/components/icons/calendar-icon'
import CarIcon from '@/components/icons/car-icon'
import ControllerIcon from '@/components/icons/controller-icon'
import HelmetIcon from '@/components/icons/helmet-icon'
import MedalIcon from '@/components/icons/medal'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { timezoneDateTime } from '@/helpers/datetime'
import { getCurrentOrNextSession, getFirstSession } from '@/helpers/events'
import useNowTicker from '@/hooks/use-now-ticker'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { CircleIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'

export default function ChampionshipCardMulti({ championship }) {
  const { events, attendees } = championship

  const firstSessionDateTimeTz = useMemo(() => {
    const fs = getFirstSession(events)
    return fs?.targetDateTime && fs?.targetTimezone
      ? timezoneDateTime({
          datetime: fs.targetDateTime,
          timezone: fs.targetTimezone,
          format: false,
        })
      : null
  }, [events])

  const champHasStarted = useMemo(
    () =>
      firstSessionDateTimeTz ? firstSessionDateTimeTz.isBefore(dayjs()) : false,
    [firstSessionDateTimeTz],
  )

  const currentOrNextMemo = useMemo(() => {
    const currOrNextSession = getCurrentOrNextSession(events)

    if (
      !currOrNextSession ||
      !currOrNextSession.targetDateTime ||
      !currOrNextSession.targetTimezone
    )
      return { currentOrNextEvent: null, currentOrNextSessionDateTimeTz: null }

    const currentOrNextEvent = events.find(
      (e) => e.id === currOrNextSession.eventId,
    )

    const currentOrNextSessionDateTimeTz = timezoneDateTime({
      datetime: currOrNextSession.targetDateTime,
      timezone: currOrNextSession.targetTimezone,
      format: false,
    })

    return { currentOrNextEvent, currentOrNextSessionDateTimeTz }
  }, [events])

  const { currentOrNextEvent, currentOrNextSessionDateTimeTz } =
    currentOrNextMemo

  const now = useNowTicker()

  const countdown = useMemo(() => {
    if (!currentOrNextSessionDateTimeTz) return ''

    const nowDate = dayjs(now)
    if (currentOrNextSessionDateTimeTz.isBefore(nowDate)) {
      return 'LIVE'
    }

    const diff = currentOrNextSessionDateTimeTz.diff(nowDate)
    const duration = dayjs.duration(diff)

    if (duration.asMinutes() < 30) {
      return `${duration.minutes()}m ${duration.seconds()}s`
    }

    return currentOrNextSessionDateTimeTz.from(nowDate, true)
  }, [currentOrNextSessionDateTimeTz, now])

  // Find the event in the championship with the next upcoming non practice session
  return (
    <div className="flex h-full flex-col rounded-sm border border-neutral-blue-700 bg-neutral-blue font-inter">
      {/* Header Image */}
      <Link
        href={`/c/${championship.community.slug}/${championship.slug}`}
        prefetch={false}
        className={cn('relative grid aspect-video', {
          'opacity-30': !currentOrNextSessionDateTimeTz,
        })}
      >
        <Image
          fill
          sizes="(min-width: 1280px) 24vw, (min-width: 768px) 33vw, 92vw"
          className="object-cover"
          src={
            championship.headerImgUrl ||
            championship.community.headerImgUrl ||
            '/img/default/championship-header.webp'
          }
          alt="Championship Header Image"
        />
      </Link>

      {/* Info */}
      <div className="flex flex-1 flex-col justify-between px-5 pt-5 desktop-xs:px-8 desktop-xs:pt-[28px]">
        <div className="flex flex-col">
          {/* Name and Community */}
          <div className="flex flex-col gap-3 desktop-xs:gap-4">
            <Link
              href={`/c/${championship.community.slug}/${championship.slug}`}
              prefetch={false}
              className={cn(
                'truncate-1 text-h-5-sm font-semibold desktop-xs:text-h-5',
                {
                  'opacity-30': !currentOrNextSessionDateTimeTz,
                },
              )}
            >
              {championship.name}
            </Link>
            <Link
              href={`/c/${championship.community.slug}`}
              prefetch={false}
              className="flex items-center gap-2"
            >
              <div className="relative grid aspect-square size-6 overflow-hidden rounded-sm desktop-xs:size-9">
                <Image
                  fill
                  sizes="36px"
                  className="object-fill"
                  src={
                    championship.community.logoImgUrl ||
                    '/img/default/profile-pic.webp'
                  }
                  alt="Community logo"
                />
              </div>
              <p className="truncate-1 text-p-1 font-bold text-neutral-blue-200">
                {championship.community.name}
              </p>
            </Link>
          </div>
          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-1 desktop-xs:mt-5 desktop-xs:gap-2">
            <div className="flex h-8 items-center gap-1 rounded-full bg-[#EEF2FF] px-4 desktop-xs:h-9">
              <MedalIcon className="size-4 stroke-[#484BD0] stroke-2 desktop-xs:size-5" />
              <p className="text-cap font-bold text-[#484BD0] desktop-xs:text-p-1">
                Championship
              </p>
            </div>
            <div className="flex h-8 items-center gap-1 rounded-full bg-warning-50 px-4 desktop-xs:h-9">
              <ControllerIcon className="size-4 stroke-warning-700 stroke-2 desktop-xs:size-5" />
              <p className="text-cap font-bold text-warning-700 desktop-xs:text-p-1">
                {championship.game.nicename}
                {championship.platforms.length > 0 && (
                  <>
                    {' - '}
                    {getPlatformName(championship.platforms)}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          {/* Details */}
          <div className="mt-4 flex flex-col gap-3 last:mb-[17px] desktop-xs:last:mb-[30px]">
            <div className="divide bg-neutral-blue-700" />
            {/* Starts or Finished */}
            <div
              className={cn('flex items-center justify-between gap-4', {
                'self-center':
                  !currentOrNextSessionDateTimeTz || !championship.isRecruiting,
              })}
            >
              <div className="flex items-center gap-2">
                <div className="grid size-6 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-9">
                  <CalendarIcon className="size-4 stroke-neutral-blue-200 stroke-2" />
                </div>
                <p
                  className={cn(
                    'font-semibold desktop-xs:text-p-2',
                    {
                      'text-white':
                        currentOrNextSessionDateTimeTz &&
                        championship.isRecruiting,
                      'text-error-500':
                        !currentOrNextSessionDateTimeTz ||
                        !championship.isRecruiting,
                    },
                    'text-p-1!',
                  )}
                >
                  {currentOrNextSessionDateTimeTz &&
                  championship.isRecruiting &&
                  !champHasStarted
                    ? 'Starts'
                    : currentOrNextSessionDateTimeTz &&
                        championship.isRecruiting &&
                        champHasStarted
                      ? 'Started'
                      : currentOrNextSessionDateTimeTz
                        ? 'Closed'
                        : 'Finished'}
                </p>
              </div>
              {currentOrNextSessionDateTimeTz && championship.isRecruiting && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <p className="truncate text-end text-p-1 desktop-xs:text-p-2">
                        {firstSessionDateTimeTz.format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent align="end">
                      <p className="text-p-2">
                        {firstSessionDateTimeTz.format('ddd D MMM • HH:mm (z)')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            <div className="divide bg-neutral-blue-700" />
            {/* Driver count */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="grid size-6 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-9">
                  <HelmetIcon className="size-4 stroke-neutral-blue-200 stroke-2" />
                </div>
                <p className="text-p-1 font-semibold desktop-xs:text-p-2">
                  Drivers
                </p>
              </div>
              {currentOrNextSessionDateTimeTz && championship.isRecruiting ? (
                <p className="text-end text-p-1 text-success-500 desktop-xs:text-p-2">
                  • {attendees.length}
                </p>
              ) : (
                <p className="text-p-1 text-error-500 desktop-xs:text-p-2">
                  • Closed
                </p>
              )}
            </div>

            <div className="divide bg-neutral-blue-700" />

            {/* Cars */}
            <div className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <div className="grid size-6 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-9">
                  <CarIcon className="size-4 stroke-neutral-blue-200 stroke-2" />
                </div>
                <p className="text-p-1 font-semibold desktop-xs:text-p-2">
                  Cars
                </p>
              </div>
              <div className="flex text-end text-p-1 desktop-xs:text-p-2">
                {championship.cars.length > 1 ? (
                  //one name and a tooltip
                  <>
                    <span className="truncate-1 break-all">
                      {championship.cars[0].car.name}{' '}
                    </span>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span className="ml-2 shrink-0 cursor-pointer text-neutral-blue-300 underline underline-offset-2">
                            +{championship.cars.length - 1} More
                          </span>
                        </TooltipTrigger>
                        <TooltipContent align="end">
                          <div className="max-h-[300px] overflow-y-auto">
                            {championship.cars.slice(1).map((car) => (
                              <p
                                key={`car-${car.car.id}`}
                                className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                              >
                                {car.car.name}
                              </p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>{' '}
                  </>
                ) : championship.cars.length === 1 ? (
                  //one name
                  <p>{championship.cars[0].car.name}</p>
                ) : championship.carClasses?.length > 1 ? (
                  <>
                    <span className="truncate-1 break-all">
                      {championship.carClasses[0].carClass.name}
                    </span>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <span className="ml-2 shrink-0 cursor-pointer text-neutral-blue-300 underline underline-offset-2">
                            +{championship.carClasses.length - 1} More
                          </span>
                        </TooltipTrigger>
                        <TooltipContent align="end">
                          <div className="max-h-[300px] overflow-y-auto">
                            {championship.carClasses.slice(1).map((cc) => (
                              <p
                                key={`car-${cc.carClass.id}`}
                                className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                              >
                                {cc.carClass.name}
                              </p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                ) : championship.carClasses?.length === 1 ? (
                  <p>{championship.carClasses[0].carClass.name}</p>
                ) : (
                  <p>N/A</p>
                )}
              </div>
            </div>

            <div className="divide bg-neutral-blue-700" />

            {/* Events */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="grid size-6 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-9">
                  <CircleIcon className="size-4 stroke-neutral-blue-200 stroke-2" />
                </div>
                <p className="text-p-1 font-semibold desktop-xs:text-p-2">
                  Events
                </p>
              </div>
              <p className="text-end text-p-1 desktop-xs:text-p-2">
                {currentOrNextEvent
                  ? currentOrNextEvent.eventRoundNumber
                  : championship.events.length}{' '}
                of {championship.events.length}
              </p>
            </div>

            <Button
              asChild
              className={cn(
                'rounded-sm! bg-neutral-blue-700! font-inter! text-p-2! font-semibold! text-white! hover:border-shades-0! hover:bg-shades-0! hover:text-black! grid h-[46px] w-full border transition-all duration-300 ease-in-out desktop-xs:h-[62px]',
                {
                  'border-neutral-blue-600!': currentOrNextSessionDateTimeTz,
                  'border-success-500!': !currentOrNextSessionDateTimeTz,
                },
              )}
            >
              <Link
                href={`/c/${championship.community.slug}/${championship.slug}${!currentOrNextSessionDateTimeTz ? '?tabName=Standings' : ''}`}
              >
                {currentOrNextSessionDateTimeTz
                  ? 'View Championship'
                  : 'View Results'}
              </Link>
            </Button>
          </div>

          {/* Next session starts */}
          {currentOrNextSessionDateTimeTz && countdown === 'LIVE' ? (
            //live
            <div className="relative mx-auto mb-4 mt-3 h-5 text-center font-bold desktop-xs:mb-6 desktop-xs:mt-4">
              <p className="absolute font-syncopate uppercase text-success">
                LIVE
              </p>
              <p className="animate-[ping_2s_cubic-bezier(0,0,0.2,2)_infinite] font-syncopate uppercase text-success">
                LIVE
              </p>
            </div>
          ) : currentOrNextSessionDateTimeTz && countdown ? (
            //upcoming
            <p className="mb-4 mt-3 h-5 self-center text-p-1 text-neutral-blue-300 desktop-xs:mb-6 desktop-xs:mt-4">
              Next race starts in {countdown}
            </p>
          ) : (
            //finished
            <div className="mb-4 mt-3 h-5 min-h-5 desktop-xs:mb-6 desktop-xs:mt-4" />
          )}
        </div>
      </div>
    </div>
  )
}
